import { createContext, useContext, useState, ReactNode, useCallback } from "react";

export type ActivePanel = "aiChat" | "taskVoice" | "scoutingVoice";

export interface AssistantContextValue {
  isPanelOpen: boolean;
  setIsPanelOpen: (open: boolean) => void;
  togglePanel: () => void;
  activePanel: ActivePanel;
  setActivePanel: (panel: ActivePanel) => void;
}

export const AssistantContext = createContext<AssistantContextValue | undefined>(undefined);

export function AssistantProvider({ children }: { children: ReactNode }) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [activePanel, setActivePanel] = useState<ActivePanel>("taskVoice");

  const togglePanel = useCallback(() => {
    setIsPanelOpen((prev) => {
      // Al ABRIR se vuelve a la ÚLTIMA pestaña usada — quien chatea con el
      // asistente reabre en el chat; quien dicta tareas, en el micro. La única
      // excepción es el muestreo, que es un flujo con pasos a medias: reabrir
      // ahí dejaría al usuario en mitad de un asistente que ya no recuerda.
      // (Idempotente: seguro aunque el updater corra dos veces en StrictMode.)
      if (!prev) setActivePanel((p) => (p === "scoutingVoice" ? "taskVoice" : p));
      return !prev;
    });
  }, []);

  return (
    <AssistantContext.Provider
      value={{ isPanelOpen, setIsPanelOpen, togglePanel, activePanel, setActivePanel }}
    >
      {children}
    </AssistantContext.Provider>
  );
}

export function useAssistantContext() {
  const context = useContext(AssistantContext);
  if (!context) {
    throw new Error("useAssistantContext must be used within an AssistantProvider");
  }
  return context;
}
